/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import {
  InjectionToken,
  Signal,
  WritableSignal,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { Auth, User, user } from '@angular/fire/auth';
import { Observable, Subject, filter, map, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { OSKUser, OSKUserAccount } from '../types/user/user.type';
import {
  Firestore,
  Unsubscribe,
  collection,
  collectionData,
  doc,
  onSnapshot
} from '@angular/fire/firestore';
import { OSKFirebaseUser, OSKCurrentUser } from '../types/user/current-user.type';
import { OSKUserOrganization } from '../types/user/user-organization.type';
import { NavigationEnd, Router } from '@angular/router';
import { OSKErrorService } from '../error-handler/error.service';
import { generateOskeyAdminDefaultMenu } from 'src/app/features/portals/sidemenu/utils/generate-admin-default-menu.util';
// import { OSKUserDefaultSidemenu } from 'src/app/features/portals/sidemenu/constants/user-menu.constant';
// import { generateUserOrganizationDefaultMenu } from 'src/app/features/portals/sidemenu/utils/generate-user-organization-default-menu.util';

/**
 * Send invitation is only used for testing purposes by the developers. It is not available for end users.
 *
 * The component for sending invitations is under portals/user/invitations ({@link OSKSendUserInvitationComponent })
 *
 * If the user signs in with an email that is included in this list, he will see the "Send Invitation" menu in his user portal
 *
 * TODO: Remove this when not needed anymore
 */
// const emailsToShowSendInvitationsTo: string[] = [
//   'david.openshaw.ext@oskey.io',
//   'greg@oskey.io',
//   'fabien@oskey.io'
// ];

/**
 * Receives a firebase user and returns an {@link OSKFirebaseUser}
 *
 * @param { User } user The firebase {@link User} that we get the information from.
 * @returns
 */
function createOSKFirebaseUser(user: User | null): OSKFirebaseUser {
  if (!user) {
    return null;
  }

  return {
    displayName: user.displayName,
    email: user.email,
    emailVerified: user.emailVerified,
    isAnonymous: user.isAnonymous,
    phoneNumber: user.phoneNumber,
    photoURL: user.photoURL,
    providerId: user.providerId,
    tenantId: user.tenantId,
    uid: user.uid
  };
}

/**
 * Injection token that returns an observable tracking the firebase {@link User}
 */
export const OSKCurrentFirebaseUser$: InjectionToken<Observable<OSKFirebaseUser>> =
  new InjectionToken('Observable for the currently logged in user', {
    providedIn: 'root',
    factory: (): Observable<OSKFirebaseUser> => {
      const auth = inject(Auth);
      return user(auth);
    }
  });

/**
 * This injection token returns a {@link Signal} of {@link OSKCurrentUser}. It is computed based on the firebase user, the oskey user and the current url
 *
 * Tracks the firebase's user observable and the router's navigation events and converts it to signals.
 *
 * Based on the received values, gets inforamtion for the logged in user and constructs the {@link OSKCurrentUser} signal that is injected through this injection token
 */
export const OSKCurrentUserToken: InjectionToken<Signal<OSKCurrentUser>> = new InjectionToken(
  'Signal for the currently logged in user',
  {
    providedIn: 'root',
    factory: (): Signal<OSKCurrentUser> => {
      const auth = inject(Auth);
      const firestore = inject(Firestore);
      const router = inject(Router);
      const errorService = inject(OSKErrorService);

      const oskUserSignal: WritableSignal<OSKUser | undefined> = signal(undefined);

      const firebaseUser: Signal<OSKFirebaseUser> = toSignal(
        user(auth).pipe(map((u) => createOSKFirebaseUser(u)))
      );

      const navigationSignal: Signal<NavigationEnd | undefined> = toSignal(
        router.events.pipe(
          filter((e) => e instanceof NavigationEnd),
          map((e) => <NavigationEnd>e)
        )
      );

      let currentUserUid: string = '';
      let docRef = null;
      let unsubscribe: Unsubscribe | null = null;
      const unsubscribeSubject: Subject<boolean> = new Subject<boolean>();

      effect(
        () => {
          const fbUser = firebaseUser();

          if (fbUser) {
            if (currentUserUid !== fbUser.uid) {
              currentUserUid = fbUser.uid;
              docRef = doc(firestore, 'users', fbUser.uid);
              if (unsubscribe) {
                unsubscribeSubject.next(true);
                unsubscribe();
              }

              /**
               * Listens for changes in the logged in user's document and gets additional information aobut the organizations he is a member of.
               */
              unsubscribe = onSnapshot(
                docRef,
                (snapshot) => {
                  const user = <OSKUser>snapshot.data();
                  if (user) {
                    const userOrganizationsDocRef = collection(
                      firestore,
                      'users/' + fbUser.uid,
                      'organizations'
                    );

                    /**
                     * Listens for changes in the user's organizations collection and constructs the user's accounts.
                     */
                    collectionData(userOrganizationsDocRef)
                      .pipe(takeUntil(unsubscribeSubject))
                      .subscribe({
                        next: (res) => {
                          const organizations = <OSKUserOrganization[]>res;
                          user.accounts = [];
                          // user.accounts.push({
                          //   name: `${user.publicProfile.firstName} ${user.publicProfile.lastName}`,
                          //   icon: 'account_circle',
                          //   path: '/user',
                          //   routerLink: '/user/account',
                          //   userId: user.userId,
                          //   roles: [],
                          //   defaultSidemenu: OSKUserDefaultSidemenu
                          // });
                          /**
                           * Adds the "Send invitation" menu the the user's user portal default side menu based on the {@link emailsToShowSendInvitationsTo} list.
                           *
                           * Send invitation is only used for testing purposes by the developers. It is not available for end users.
                           * TODO: Remove this when not needed anymore
                           */
                          // if (emailsToShowSendInvitationsTo.includes(user.email)) {
                          // const sendInvitationMenu = user.accounts[0].defaultSidemenu.items.find(
                          //   (m) => m.routerLink === '/invitations/send'
                          // );
                          // if (!sendInvitationMenu) {
                          //   user.accounts[0].defaultSidemenu.items.unshift({
                          //     routerLink: '/invitations/send',
                          //     icon: 'person',
                          //     label: 'portals.sidemenu.user.sendInvitation'
                          //   });
                          // }
                          //}
                          // TODO: Remove the above code when not needed anymore

                          /**
                           * Creates the user account for every organization.
                           *
                           * Creates an Admin portal account or Organization portal account based on the roles the user has within that organization
                           */
                          for (const organization of organizations) {
                            const isOskeyAdmin = organization.userRoles?.some((r) =>
                              r.startsWith('v1.admin')
                            );
                            if (isOskeyAdmin) {
                              const account: OSKUserAccount = {
                                name: 'Admin (' + organization.organizationName + ')',
                                icon: 'shield-user',
                                path: '/admin',
                                routerLink: '/admin/oskey-users',
                                organizationId: organization.organizationId,
                                userId: user.userId,
                                roles: organization.userRoles,
                                defaultSidemenu: generateOskeyAdminDefaultMenu(organization)
                              };

                              user.accounts.push(account);
                            }
                            // const account: OSKUserAccount = {
                            //   name: organization.organizationName,
                            //   icon: 'corporate_fare',
                            //   path: `/organization/${organization.organizationId}`,
                            //   routerLink: `/organization/${organization.organizationId}` + '/dashboard',
                            //   organizationId: organization.organizationId,
                            //   userId: user.userId,
                            //   roles: organization.userRoles,
                            //   defaultSidemenu: generateUserOrganizationDefaultMenu(organization)
                            // };

                            //user.accounts.push(account);
                          }

                          oskUserSignal.set(user);
                        },
                        error: (error) => {
                          errorService.showError(error?.message);
                        }
                      });
                  } else {
                    oskUserSignal.set(undefined);
                  }
                },
                (error) => {
                  errorService.showError(error?.message);
                }
              );
            }
          } else {
            currentUserUid = '';
            docRef = null;
            oskUserSignal.set(undefined);
            if (unsubscribe) {
              unsubscribeSubject.next(true);
              unsubscribe();
              unsubscribe = null;
            }
          }
        },
        {
          allowSignalWrites: true
        }
      );

      let selectedAccount: OSKUserAccount | undefined = undefined;

      /**
       * The computed signal that is returned by the Injection token
       */
      const computedUserSignal = computed(
        () => {
          const fbUser = firebaseUser();
          let oskUser = oskUserSignal();
          const navigation = navigationSignal();
          /**
           * Shows if the user has an account and has set his firstName and lastName
           */
          let isOskVerified: boolean | null | undefined = undefined;

          const oskUserHasNames =
            oskUser && oskUser.publicProfile.firstName && oskUser.publicProfile.lastName;

          if (fbUser && !fbUser.isAnonymous && fbUser.emailVerified && oskUserHasNames) {
            isOskVerified = true;
          } else {
            isOskVerified = false;
          }

          if (!fbUser || !oskUser) {
            isOskVerified = undefined;
          }

          if (fbUser === null) {
            isOskVerified = null;
            oskUser = undefined;
          }

          /**
           * This is done only on {@link NavigationEnd} event to prevent flikkering and changing content in the header before we know what account we have.
           *
           * Since the user inforamtion comes asynchronously we need to wait for it to prevent showing the wrong header for a second before updating it to the one we need.
           */
          if (navigation) {
            if (!fbUser) {
              selectedAccount = undefined;
            } else {
              const accounts = oskUser?.accounts || [];
              if (accounts.length > 0) {
                if (navigation.urlAfterRedirects == '/') {
                  if (!selectedAccount) {
                    selectedAccount = accounts[0];
                  }
                } else {
                  const account = accounts.find((a) =>
                    navigation.urlAfterRedirects.startsWith(a.path)
                  );
                  if (account) {
                    if (
                      !selectedAccount ||
                      JSON.stringify(selectedAccount) !== JSON.stringify(account)
                    ) {
                      selectedAccount = account;
                    }
                  } else {
                    selectedAccount = undefined;
                  }
                }
              }
            }
          }

          return {
            firebaseUser: fbUser,
            oskUser: oskUser,
            isOskVerified: isOskVerified,
            selectedAccount: selectedAccount
          };
        },
        {
          equal: (a, b) => JSON.stringify(a) === JSON.stringify(b)
        }
      );

      return computedUserSignal;
    }
  }
);
